<template>
  <div class="account">
    <img :src="walletIcon" class="account__wallet-icon" />
    <span class="account__balance">{{ mainBalance.formatted.value }}</span>
    <p-button
      v-tooltip.bottom="walletName"
      class="account__button"
      @click="showAccounts"
    >
      <span class="account__button-address">{{ shortAddress }}</span>
      <identification-icon
        :address="address.toString()"
        class="account__button-identicon_type_desktop"
        size="20"
      />
      <identification-icon
        :address="address.toString()"
        class="account__button-identicon_type_mobile"
        size="28"
      />
    </p-button>
  </div>
</template>

<script lang="ts" setup>
import { IdentificationIcon } from '@/components/UI/IdentificationIcon';
import { computed, h, watch } from 'vue';
import { useCurrentAccountBalance } from '@/composables/useAccountBalance';
import { APTOS } from '@/constants';
import { AccountsListDialog } from '@/components/Dialogs/AccountsListDialog';
import { useDialogStore } from '@/store';
import { useDialog } from 'primevue/usedialog';
import { DialogHeader } from '@/components/UI/DialogHeader';
import { useI18n } from '@/composables/useI18n';
import { useWalletPlugin } from '@/composables/useWalletPlugin';

const { t } = useI18n();

const { account, network, wallet } = useWalletPlugin();
const dialogStore = useDialogStore();
const dialog = useDialog();
const mainBalance = useCurrentAccountBalance(APTOS);

const address = computed(() => account?.value?.address || null);

const walletIcon = computed(() => wallet?.value?.icon || null);

const shortAddress = computed(() => {
  if (address?.value && typeof address.value === 'string') {
    return address.value.slice(0, 6) + ' ... ' + address.value.slice(-4);
  }

  return '';
});

const walletName = computed(() => wallet?.value?.name);

const networkId = computed(() => network.value?.chainId);

watch(networkId, () => {
  mainBalance.refetch();
});

function showAccounts() {
  dialog.open(AccountsListDialog, {
    props: {
      modal: true,
      contentStyle: { width: '400px' },
      contentClass: 'step',
      draggable: false,
      closable: true,
      dismissableMask: true,
      showHeader: dialogStore.showHeader,
    },
    templates: {
      header: () =>
        h(DialogHeader, {
          title: t('common.connectWallet'),
        }),
    },
  });
}
</script>
<style lang="scss" scoped>
.account {
  display: flex;
  align-items: center;
  justify-content: space-between;

  height: min-content;
  margin-left: 0.5rem;
  padding: 0.25rem;

  border-radius: 70px;
  background-color: var(--surface-card);

  white-space: nowrap;
  color: #e5e4fa;
  font-size: 15px;
  font-weight: 500;
  line-height: 18px;
  letter-spacing: 0.44px;

  &__balance {
    margin-right: 0.75rem;
  }

  &__button {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;

    height: 2rem;
    padding: 0.375rem 0.75rem;

    border: none;
    border-radius: 17px;
    background-color: #3e3e52;
    cursor: pointer;
    user-select: none;
    outline: none;
    box-shadow: none;

    color: #e5e4fa;
    font: 500 15px/18px var(--font-family);
    transition: color, background-color 0.15s ease;
    will-change: color, background-color;

    &-address {
      margin-right: 0.5rem;
    }

    &:active,
    &:hover,
    &:focus,
    &:focus-visible {
      color: var(--primary-color-text);
      background-color: #535266;
    }
  }

  &__wallet-icon {
    width: 2rem;
    height: 2rem;
    margin-right: 0.625rem;
  }

  &__button-identicon {
    &_type_mobile {
      display: none;
    }

    @media screen and (max-width: 467px) {
      &_type_mobile {
        display: block;
      }

      &_type_desktop {
        display: none;
      }
    }
  }

  @media screen and (max-width: 1167px) {
    &__button {
      margin-left: auto;
    }
  }

  @media screen and (max-width: 467px) {
    padding-left: 1rem;

    &__wallet-icon {
      display: none;
    }

    &__button {
      width: 32px;
      height: 32px;
      padding: 2px;

      &-address {
        display: none;
      }
    }
  }
}
</style>
<style lang="scss">
.p-tooltip-arrow {
  border-width: 0px !important;
}
.p-tooltip-text {
  font-size: 15px;
  font-weight: 500;
  background: #28253e !important;
  border-radius: 8px !important;
}
.p-tooltip {
  margin-top: 0.5rem;
}
</style>
